import React, { useEffect, useState } from "react";
import {
	Control,
	Controller,
	FormState,
	useFieldArray,
	UseFormGetValues,
	UseFormSetValue,
	UseFormTrigger,
	useWatch,
} from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useStoreState } from "ezStore";

import { Button, Card, Checkbox, FormGroup, Input, Select, Textarea } from "@cofano/cck";
import { Alert, CircularProgress, Grid2 as Grid } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import NumberInput from "components/cck/NumberInput";
import FormField from "components/cck/FormField";
import DatePicker from "components/cck/DatePicker";
import LocationSelect from "view/locations/components/selects/LocationSelect";
import RelationSelect from "view/relations/components/selects/RelationSelect";
import {
	calculateNetWeightAndVolume,
	getActionsToCalculate,
	getCallTypeColor,
	validateActionTypeSequence,
} from "utils/cargoActionUtils";
import {
	DataFormat,
	Fields,
	hasOptionalFieldPermission,
	hasPluginPermission,
	Language,
	usePermission,
} from "imports/Utils";
import { DateUtils } from "utils/DateUtils";
import { CallType, LocationType, RelationType } from "__generated__/enums";
import { ConnectingShipPortVisit } from "__generated__/graphql";
import { CargoActionForm, CargoForm } from "types/index";
import LimitTypeSelect from "view/cargos/components/createEdit/LimitTypeSelect";
import moment from "moment";
import { defaultCargoAction } from "view/cargos/utils/CargoUtils";
import ShipSelect from "view/ships/components/selects/ShipSelect";
import { AddRounded } from "@mui/icons-material";
import { gql } from "__generated__/gql";
import client from "../../../../apollo";
import { useLazyQuery } from "@apollo/client";
import { GetBatchRoutesDocument } from "__generated__/view/maps/graphql/map.queries.generated";
import { numberOrNull, round } from "utils/numberUtils";
import {
	getOrderedActions,
	previewSimulatedTimestamps,
} from "view/cargos/utils/CargoActionUtils";

type ActionsProps = {
	defaultIncludedActionTypes?: CallType[];
	calculateWeightAndVolume: boolean;
	control: Control<CargoForm>;
	formState: FormState<CargoForm>;
	setValue: UseFormSetValue<CargoForm>;
	validFrom?: number | null;
	validUntil?: number | null;
	trigger: UseFormTrigger<CargoForm>;
	getValues: UseFormGetValues<CargoForm>;
	validateCargoActions: () => boolean;
};

const Actions = ({ defaultIncludedActionTypes = [], calculateWeightAndVolume, control, formState, setValue, validFrom, validUntil, trigger, getValues, validateCargoActions }: ActionsProps) => {

	const session = useStoreState(store => store.session);

	// Store route info per action index (route between action[i] and action[i+1])
	const [ routeInfoMap, setRouteInfoMap ] = useState<Record<number, Route>>({});

	const { isRTR, isSkipperOrShipManager, isCustomer } = usePermission();

	const useSimulatedTimestamps = hasPluginPermission(session, "cargoActionUseSimulatedTimestamps");

	const values = useWatch({ control });
	const actions = useWatch({ control, name: "actions" }) ?? [];
	const actionsPreview = previewSimulatedTimestamps(actions, routeInfoMap);

	const { fields, append, remove, replace } = useFieldArray({
		control,
		name: "actions",
		keyName: "generatedId",
	});

	const getPossibleActionTypes = (index: number): CallType[] => {
		const sequence = actions.map(a => a.type);
		const possibleActionTypes = [];

		if (index == 0) {
			// first action can always be loading
			possibleActionTypes.push(CallType.Loading);

			// first action can be purging if enabled
			if (values.isNitrogenPurgingRequired) {
				possibleActionTypes.push(CallType.NitrogenPurging);
			}

		} else if (index == sequence.length - 1) {
			// last action can always be discharging
			possibleActionTypes.push(CallType.Discharging);

			// last action can be cleaning if enabled
			if (values.isCleaningRequired) {
				possibleActionTypes.push(CallType.Cleaning);
			}
		} else {
			// middle actions can be loading or discharging
			possibleActionTypes.push(CallType.Loading);
			possibleActionTypes.push(CallType.Discharging);
		}

		return possibleActionTypes;
	};

	const onAdd = () => {
		append({ ...defaultCargoAction(CallType.Discharging) });
	};

	const canDeleteCargoAction = (index: number): boolean => {
		if (actions.length <= 2) {
			return false;
		}

		const newActions = actions.filter((_, i) => i !== index);

		// we should always have at least 1 loading and 1 discharging action
		const newTypes = newActions.map(x => x.type ?? CallType.Loading);
		const hasLoadingAction = newTypes.includes(CallType.Loading);
		const hasDischargingAction = newTypes.includes(CallType.Discharging);
		if (!hasLoadingAction || !hasDischargingAction) {
			return false;
		}

		return newActions.every((x, i) => validateActionTypeSequence(
			newActions,
			i,
			x.type ?? CallType.Loading,
			values.isNitrogenPurgingRequired ?? false,
			values.isCleaningRequired ?? false,
		),
		);
	};

	const onWeightChange = (value: number | null, index: number) => {

		const densityKgM3 = values.product?.densityKgM3 || 1;

		const weight = value ?? 0;
		const volume = round(weight / densityKgM3, 3);

		actions[index].weight = Number(weight);
		actions[index].volume = Number(volume);

		const actionsToCalculate = getActionsToCalculate(actions, index);

		const [ netWeight, netVolume ] = calculateNetWeightAndVolume(actionsToCalculate);

		setValue(`actions.${index}.volume`, volume || null);

		if (index == 0) {
			if (actions[1] && actions[0].type !== actions[1].type) {
				setValue(`actions.${index + 1}.weight`, weight || null);
				setValue(`actions.${index + 1}.volume`, volume || null);
			}
		} else {
			if (actions[index + 1] && actions[index + 1].type === "DISCHARGING" && netWeight > 0) {
				setValue(`actions.${index + 1}.weight`, netWeight || null);
				setValue(`actions.${index + 1}.volume`, netVolume || null);
			}
		}
	};

	const onVolumeChange = (value: number | null, index: number) => {
		const densityKgM3 = values.product?.densityKgM3 || 1;

		const volume = value ?? 0;
		const weight = round(volume * densityKgM3, 3);

		setValue(`actions.${index}.weight`, weight || null);

		if (index === 0 && actions[1] && actions[0].type !== actions[1].type) {
			setValue(`actions.${index + 1}.volume`, volume || null);
			setValue(`actions.${index + 1}.weight`, weight || null);
		}
	};

	const initShipToShip = (index: number) => {
		setValue(`actions.${index}.shipToShip`, true);
		clearConnectingVisit(index);
	};

	const clearShipToShip = (index: number) => {
		setValue(`actions.${index}.shipToShip`, false);
		clearConnectingVisit(index);
	};

	const initConnectingVisit = (index: number) => {
		initShipToShip(index);

		setValue(`actions.${index}.connectingShipPortVisit.ship`, null);
		setValue(`actions.${index}.connectingShipPortVisit.id`, undefined);
		setValue(`actions.${index}.connectingShipPortVisit.existingConnectingShipPortVisit`, undefined);
		setValue(`actions.${index}.connectingShipPortVisit.agent`, null);

		const windowFrom =
			actions[index].windowFrom ?? DateUtils.getTimestamp(moment(actions[index].date).startOf("day"));

		const windowTo = actions[index].windowTo ?? DateUtils.getTimestamp(moment(actions[index].date).endOf("day"));

		setValue(`actions.${index}.connectingShipPortVisit.from`, windowFrom);
		setValue(`actions.${index}.connectingShipPortVisit.until`, windowTo);

		let portLocation = null;

		if (actions[index].location) {
			const location = actions[index].location;

			if (location.description && location.type) {
				const port = location.locationGroups?.find(x => x.type == LocationType.Port) ?? location;

				if (port.id && port.description && port.type) {
					portLocation = {
						id: port.id,
						description: port.description,
						type: port.type,
						locationGroups: [],
					};
				}
			}
		}

		setValue(`actions.${index}.connectingShipPortVisit.location`, portLocation);
	};

	const clearConnectingVisit = (index: number) => {
		clearBerth(index);

		setValue(`actions.${index}.connectingShipPortVisit.ship`, null);
		setValue(`actions.${index}.connectingShipPortVisit.id`, undefined);
		setValue(`actions.${index}.connectingShipPortVisit.existingConnectingShipPortVisit`, undefined);
		setValue(`actions.${index}.connectingShipPortVisit.location`, null);
		setValue(`actions.${index}.connectingShipPortVisit.from`, null);
		setValue(`actions.${index}.connectingShipPortVisit.until`, null);
		setValue(`actions.${index}.connectingShipPortVisit.agent`, null);

		setValue(`actions.${index}.connectingVisitNotes`, null);
	};

	const initBerth = (index: number) => {
		clearBerth(index);

		const action = actions[index];
		const location = actions[index].location;
		setValue(`actions.${index}.connectingShipBerthing.visit.id`, action.connectingShipPortVisit?.id);
		setValue(`actions.${index}.connectingShipBerthing.location`, (location?.id != null && location.description != null && location.type != null) ? { id: location.id, description: location.description, type: location.type, locationGroups: [] } : undefined);
		setValue(`actions.${index}.connectingShipBerthing.window.from`, action.connectingShipPortVisit?.from ?? undefined);
		setValue(`actions.${index}.connectingShipBerthing.window.to`, action.connectingShipPortVisit?.until ?? undefined);
	};

	const clearBerth = (index: number) => {
		setValue(`actions.${index}.connectingShipBerthing`, undefined);
	};

	const recalculateDates = () => {
		for (let i = 0; i < actions.length - 1; i++) {
			const currentAction = actions[i];
			const nextAction = actions[i + 1];
			const route = routeInfoMap[i];

			if (!route || !route.transitTimeInMinutes) {
				continue;
			}

			if (!currentAction?.date) {
				continue;
			}

			// Skip if next action already has a date (don't overwrite)
			if (nextAction?.date) {
				continue;
			}

			const calculatedDate = moment(currentAction.date)
				.add(route.transitTimeInMinutes + 1440, "minutes")
				.format("YYYY-MM-DD");

			setValue(`actions.${i + 1}.date`, calculatedDate);
		}
	};

	const reorderActions = () => {
		const reorderedActions = getOrderedActions(actions, useSimulatedTimestamps);
		const needsReorder = reorderedActions.some((x, i) => x !== actions[i]);

		if (needsReorder) {
			replace(reorderedActions);
			trigger("actions");
		}
	};

	const canSelectActionType = (index: number, actionType: CallType): boolean => {
		return validateActionTypeSequence(actions, index, actionType, values.isNitrogenPurgingRequired ?? false, values.isCleaningRequired ?? false);
	};

	// Tracks first date + all locations (excludes calculated dates to prevent infinite loop)
	const actionsSignature = actions.map((action, i) => i === 0
		? `${action.date || ""}:${action.location?.id || ""}`
		: `${action.location?.id || ""}`,
	).join("|");

	// Tracks route transit times for all action pairs
	const routeInfoSignature = Object.entries(routeInfoMap)
		.map(([ index, route ]) => `${index}:${route?.transitTimeInMinutes || 0}`)
		.join("|");

	useEffect(() => {
		recalculateDates();
		reorderActions();
	}, [ actionsSignature, routeInfoSignature ]);

	useEffect(() => {
		if (defaultIncludedActionTypes.length > 0) {
			actions.forEach((action, index) => {
				if (action.id == null) {
					const includedForLaytimeCalculation = action.type != null && defaultIncludedActionTypes?.includes(action.type);

					setValue(`actions.${index}.includedForLaytimeCalculation`, includedForLaytimeCalculation);
				}
			});
		}

	}, [ defaultIncludedActionTypes?.length ]);

	return (
		<div className="cargo-form-actions">
			<div style={{ alignItems: "center", display: "flex", marginBottom: 16 }}>
				<div style={{ marginRight: 24 }}>
					<Button
						icon={<AddIcon />}
						onClick={onAdd}
						variant="outlined"
						size="small" />
				</div>
			</div>

			<div className="cargo-actions">
				{fields.map((item, index) => {
					const action = actions[index];
					if (!action) {
						return null;
					}

					const actionPreview = actionsPreview[index];

					const type = action.type;
					const weight = action.weight;
					const volume = action.volume;

					const currentLocation = action.location;
					const nextLocation = actions[index + 1]?.location;

					const canHaveShipToShip = (type === CallType.Loading || type === CallType.Discharging)
						&& hasOptionalFieldPermission(session, Fields.CARGO_ACTION, "shipToShip");
					const isShipToShip = canHaveShipToShip && !!action.shipToShip;

					const connectingVisit = action.connectingShipPortVisit;

					const hasConnectingShipVisit =
						!!connectingVisit?.ship ||
						!!connectingVisit?.existingConnectingShipPortVisit ||
						!!connectingVisit?.id ||
						!!connectingVisit?.location ||
						!!connectingVisit?.from ||
						!!connectingVisit?.until;

					const hasConnectingShipBerthing = !!action.connectingShipBerthing;

					return (
						<div
							className="cargo-action edit"
							key={item.id || item.generatedId}>
							<Card
								title={intl.formatMessage({ id: action.type || "LOADING" })}
								subtitle={action.location?.description}
								actions={[ {
									onClick: () => remove(index),
									hidden: !canDeleteCargoAction(index),
									icon: <DeleteIcon />,
								} ]}>
								<React.Fragment>
									<FormGroup label={Language("TYPE")} overflow="hidden">
										<FormField errors={formState.errors} name={`actions.${index}.type`}>
											<Controller
												name={`actions.${index}.type`}
												control={control}
												rules={{
													required: true,
												}}
												render={({ field }) => (
													<Select
														clearable={false}
														getOptionDisabled={(option) => !canSelectActionType(index, option)}
														fetchOptions={getPossibleActionTypes(index)}
														getOptionLabel={option => intl.formatMessage({ id: option })}
														getOptionColor={getCallTypeColor}
														value={field.value}
														onChange={field.onChange}
														disableSearch
													/>
												)} />
										</FormField>
									</FormGroup>

									<FormGroup label={Language("LOCATION")} overflow="hidden" required>
										<Grid container spacing={2} style={{ marginBottom: 12 }}>
											<Grid size="grow">
												<FormField errors={formState.errors} name={`actions.${index}.location`}>
													<Controller
														name={`actions.${index}.location`}
														control={control}
														rules={{ required: true }}
														render={({ field }) => (
															<LocationSelect
																{...field}
																types={[
																	LocationType.Terminal,
																	LocationType.Area,
																	LocationType.Port,
																	LocationType.InlandTerminal,
																	LocationType.Waterway,
																	LocationType.Buoy,
																	LocationType.ExtractionMachine,
																]}
																onChange={option => {
																	const port = option?.locationGroups?.find(x => x.type == LocationType.Port) ?? option;
																	setValue(`actions.${index}.connectingShipPortVisit.location`, port ? { id: port.id, description: port.description, type: port.type, locationGroups: [] } : null);
																	field.onChange(option);
																}}
																additionalSearchOptions={{
																	isActive: true,
																}}
															/>
														)} />
												</FormField>
											</Grid>

											{canHaveShipToShip && (
												<Grid sx={{ alignItems: "center", display: "flex" }}>
													<Controller name={`actions.${index}.shipToShip`}
														control={control}
														render={({ field }) => (
															<Checkbox
																{...field}
																label={Language("SHIP_TO_SHIP")}
																value={undefined}
																checked={field.value || undefined}
																onChange={(event) => {
																	const checked = event.target.checked;
																	field.onChange(checked);

																	if (!checked) {
																		clearShipToShip(index);
																	} else {
																		initShipToShip(index);
																	}
																}}
															/>
														)}
													/>
												</Grid>
											)}
										</Grid>
									</FormGroup>

									<ActionDateTimeWindowPicker
										control={control}
										index={index}
										action={action}
										actions={actions}
										actionPreview={actionPreview}
										formState={formState}
										setValue={setValue}
										getValues={getValues}
										trigger={trigger}
										validFrom={validFrom}
										validUntil={validUntil}
										useSimulatedTimestamps={useSimulatedTimestamps}
										validateCargoActions={validateCargoActions}
									/>

									{(type === CallType.Loading || type === CallType.Discharging) && (
										<Grid container spacing={2} style={{ marginBottom: 12 }}>
											<Grid size={{ lg: 6, md: 6, xs: 12 }}>
												<FormGroup label={Language("WEIGHT")} overflow="hidden">
													<Controller
														name={`actions.${index}.weight`}
														control={control}
														render={({ field: { onChange } }) => (
															<NumberInput
																onChange={value => {
																	onChange(value);

																	if (calculateWeightAndVolume) {
																		onWeightChange(value, index);
																	}
																}}
																disabled={!values.isWeightPrimary}
																value={weight || null}
																decimalScale={3}
																addonAfter={intl.formatMessage({ id: "UNIT_TON" })} />
														)} />
												</FormGroup>
											</Grid>

											<Grid size={{ lg: 6, md: 6, xs: 12 }}>
												{hasOptionalFieldPermission(session, Fields.ORDER, "volume") && (
													<FormGroup label={Language("CARGO_VOLUME")} overflow="hidden">
														<Controller
															name={`actions.${index}.volume`}
															control={control}
															render={({ field: { onChange } }) => (
																<NumberInput
																	onChange={value => {
																		onChange(value);

																		if (calculateWeightAndVolume) {
																			onVolumeChange(value, index);
																		}
																	}}
																	disabled={!values.isVolumePrimary}
																	value={volume || null}
																	decimalScale={3}
																	addonAfter={intl.formatMessage({ id: "UNIT_M3" })} />
															)} />
													</FormGroup>
												)}
											</Grid>
										</Grid>
									)}

									{(type === CallType.Loading || type === CallType.Discharging) && hasPluginPermission(session, "surveyors") && (
										<FormGroup label={intl.formatMessage({ id: "SURVEYOR" })} overflow="hidden">
											<Controller
												name={`actions.${index}.surveyor`}
												control={control}
												render={({ field }) => (
													<RelationSelect
														{...field}
														types={[ RelationType.Surveyor ]}
														onChange={option => field.onChange(option ? { id: option.id, name: option.name } : null)} />
												)} />
										</FormGroup>
									)}

									{type === CallType.NitrogenPurging && (
										<FormGroup label={intl.formatMessage({ id: "CARGO_ACTION_LIMIT" })} overflow="hidden">
											<Grid container spacing={2}>
												<Grid size={5}>
													<Controller
														name={`actions.${index}.limitValue`}
														control={control}
														render={({ field }) => (
															<Input
																{...field}
																value={field.value || ""}
																type="number"
																addonAfter={DataFormat.limitValue(actions[index]?.limitType)}
															/>
														)} />
												</Grid>
												<Grid size={7}>
													<Controller
														name={`actions.${index}.limitType`}
														control={control}
														render={({ field }) => <LimitTypeSelect {...field} />}
													/>
												</Grid>
											</Grid>
										</FormGroup>
									)}

									<FormGroup label={intl.formatMessage({ id: "REFERENCE" })} overflow="hidden">
										<Controller
											name={`actions.${index}.reference`}
											control={control}
											render={({ field }) => <Textarea {...field} />} />
									</FormGroup>

									<FormGroup label={intl.formatMessage({ id: "CARGO_ACTION_NOTES" })} overflow="hidden">
										<Controller
											name={`actions.${index}.notes`}
											control={control}
											render={({ field }) => <Textarea {...field} />} />
									</FormGroup>

									<Controller
										name={`actions.${index}.includedForLaytimeCalculation`}
										control={control}
										render={({ field }) => (
											<Checkbox
												{...field}
												label={intl.formatMessage({ id: "INCLUDED_FOR_LAYTIME_CALCULATION" })}
												value={undefined}
												checked={field.value}
											/>
										)}
									/>
								</React.Fragment>
							</Card>

							{isShipToShip && (
								<div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 16 }}>
									{!hasConnectingShipVisit && (
										<Grid>
											<div
												className="add-ship-to-ship"
												onClick={() => initConnectingVisit(index)}>
												<AddRounded />
												{intl.formatMessage({ id: "ADD_CONNECTING_VISIT" })}
											</div>
										</Grid>
									)}

									{hasConnectingShipVisit && (
										<Card
											title={`${intl.formatMessage({ id: "CONNECTING_VISIT" })} ${action.connectingShipPortVisit?.ship?.name ?? ""}`}
											actions={[ {
												onClick: () => clearConnectingVisit(index),
												icon: <DeleteIcon />,
											} ]}>
											<ConnectingVisit
												action={action}
												actionIndex={index}
												control={control}
												formState={formState}
												setValue={setValue}
												connectingShipPortVisit={actions[index].connectingShipPortVisit as ConnectingShipPortVisit | undefined}
											/>
										</Card>
									)}

									{hasConnectingShipVisit && !hasConnectingShipBerthing && (
										<Grid>
											<div
												className="add-ship-to-ship"
												onClick={() => initBerth(index)}>
												<AddRounded />
												{intl.formatMessage({ id: "ADD_BERTH" })}
											</div>
										</Grid>
									)}

									{hasConnectingShipBerthing && (
										<Card
											title={`${intl.formatMessage({ id: "BERTH" })} ${action.connectingShipBerthing?.location?.description ?? ""}`}
											actions={[ {
												onClick: () => clearBerth(index),
												icon: <DeleteIcon />,
											} ]}>
											<Berth
												action={action}
												actionIndex={index}
												control={control}
												formState={formState}
												setValue={setValue} />
										</Card>
									)}
								</div>
							)}


							{(isRTR || isSkipperOrShipManager || isCustomer) && currentLocation && nextLocation && (currentLocation.id !== nextLocation.id) && (
								<RouteInfo
									setRouteInfo={(route) => setRouteInfoMap(prev => ({ ...prev, [index]: route }))}
									currentLocationId={currentLocation.id}
									nextLocationId={nextLocation.id} />
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
};

type ConnectingVisitProps = {
	formState: FormState<CargoForm>;
	control: Control<CargoForm>;
	action: CargoActionForm;
	actionIndex: number;
	setValue: UseFormSetValue<CargoForm>;
	connectingShipPortVisit: ConnectingShipPortVisit | undefined;
};
const ConnectingVisit = (
	{
		formState,
		control,
		action,
		actionIndex,
		setValue,
		connectingShipPortVisit,
	}: ConnectingVisitProps) => {

	const values = useWatch({ control });

	const visitId = action.connectingShipPortVisit?.existingConnectingShipPortVisit?.id ?? action.connectingShipPortVisit?.id;

	const syncVisitField = (
		field: "agent" | "from" | "until",
		value: number | { id: number; name: string; } | null | undefined,
	) => {
		if (visitId == null) {
			return;
		}
		values.actions?.forEach((other, i) => {
			if (i === actionIndex) {
				return;
			}
			const otherId = other.connectingShipPortVisit?.existingConnectingShipPortVisit?.id ?? other.connectingShipPortVisit?.id;
			if (otherId === visitId) {
				setValue(`actions.${i}.connectingShipPortVisit.${field}`, value);
			}
		});
	};

	const getActionDateRange = () => {
		const useRange = action.displayAsWindow ?? (action.windowFrom != null);
		if (useRange) {
			return { from: action.windowFrom, until: action.windowTo };
		} else if (action.date) {
			const dateTimestamp = DateUtils.getTimestamp(action.date);
			return { from: dateTimestamp, until: dateTimestamp };
		}
		return { from: null, until: null };
	};

	const getPortStayWindow = () => {
		const existingVisit = action.connectingShipPortVisit?.existingConnectingShipPortVisit;
		if (existingVisit) {
			return { from: existingVisit.from, until: existingVisit.until };
		}
		return {
			from: action.connectingShipPortVisit?.from,
			until: action.connectingShipPortVisit?.until,
		};
	};

	const isActionDateOutsidePortStayWindow = () => {
		const { from: actionFrom, until: actionUntil } = getActionDateRange();
		const { from: portStayFrom, until: portStayUntil } = getPortStayWindow();

		if (actionFrom == null || actionUntil == null || portStayFrom == null || portStayUntil == null) {
			return false;
		}
		return actionFrom < portStayFrom || actionUntil > portStayUntil;
	};

	const calculateExtendedPortStayWindow = () => {
		const { from: actionFrom, until: actionUntil } = getActionDateRange();
		const { from: portStayFrom, until: portStayUntil } = getPortStayWindow();

		if (portStayFrom == null || portStayUntil == null) {
			return { from: actionFrom, until: actionUntil };
		}

		return {
			from: actionFrom != null && actionFrom < portStayFrom ? actionFrom : portStayFrom,
			until: actionUntil != null && actionUntil > portStayUntil ? actionUntil : portStayUntil,
		};
	};

	const extendPortStayWindow = () => {
		const extendedWindow = calculateExtendedPortStayWindow();
		setValue(`actions.${actionIndex}.connectingShipPortVisit.from`, extendedWindow.from);
		setValue(`actions.${actionIndex}.connectingShipPortVisit.until`, extendedWindow.until);
	};

	const fetchConnectingVisits = async (_: string) => {

		const result = await client.query({
			query: CONNECTING_SHIP_PORT_VISIT_QUERY,
			variables: {
				search: {
					ships: [ action.connectingShipPortVisit!.ship!.id! ],
					locations: [ action.location!.id!, ...action.location!.locationGroups!.map(l => l.id) ],
				},
			},
		});

		return result.data.connectingShipPortVisits;
	};

	const [ connectingVisitType, setConnectingVisitType ] = useState<"NEW" | "EXISTING">(
		action.connectingShipPortVisit?.existingConnectingShipPortVisit ? "EXISTING" : "NEW",
	);

	useEffect(() => {
		const shipId = action.connectingShipPortVisit?.ship?.id;

		if (!shipId) {
			setConnectingVisitType("NEW");
			setValue(`actions.${actionIndex}.connectingShipPortVisit.id`, undefined);
			setValue(`actions.${actionIndex}.connectingShipPortVisit.existingConnectingShipPortVisit`, undefined);
			return;
		}

		fetchConnectingVisits("").then((response) => {
			if (!response?.length) {
				return;
			}

			const currentVisit = values.actions?.[actionIndex]?.connectingShipPortVisit;
			const selectedExistingVisit = currentVisit?.existingConnectingShipPortVisit;

			if (selectedExistingVisit) {
				setConnectingVisitType("EXISTING");
				return;
			}

			if (currentVisit?.id != null) {
				const foundVisit = response.find(x => x.id === currentVisit.id);
				if (foundVisit) {
					setConnectingVisitType("EXISTING");
					setValue(`actions.${actionIndex}.connectingShipPortVisit.existingConnectingShipPortVisit`, foundVisit);
					return;
				}
			}

			setConnectingVisitType("EXISTING");

			if (response.length === 1) {
				setValue(`actions.${actionIndex}.connectingShipPortVisit.existingConnectingShipPortVisit`, response[0]);
			}
		});
	}, [ action.connectingShipPortVisit?.ship?.id ]);

	return <>
		<FormGroup label={Language("CONNECTING_SHIP")} required>
			<Grid container spacing={2}>
				<Grid size={8}>
					<FormField
						errors={formState.errors}
						name={`actions.${actionIndex}.connectingShipPortVisit.ship`}
					>
						<Controller
							name={`actions.${actionIndex}.connectingShipPortVisit.ship`}
							control={control}
							rules={{ required: true }}
							render={({ field }) =>
								<ShipSelect
									{...field}
									value={field.value ?? null}
									clearable
									disabled={!!connectingShipPortVisit?.id}
									onChange={(option) => {
										field.onChange(option ?? null);
										setConnectingVisitType("NEW");
										setValue(`actions.${actionIndex}.connectingShipPortVisit.id`, undefined);
										setValue(`actions.${actionIndex}.connectingShipPortVisit.existingConnectingShipPortVisit`, undefined);
									}}
								/>
							}
						/>
					</FormField>
				</Grid>

				<Grid size={4}>
					<Select
						fetchOptions={[ "NEW", "EXISTING" ]}
						getOptionLabel={(option) => intl.formatMessage({ id: option })}
						clearable={false}
						disableSearch
						disabled={action.connectingShipPortVisit?.ship == null}
						value={connectingVisitType}
						onChange={(option) => {
							if (option === "NEW") {
								setValue(`actions.${actionIndex}.connectingShipPortVisit.id`, undefined);
								setValue(`actions.${actionIndex}.connectingShipPortVisit.existingConnectingShipPortVisit`, undefined);
							}

							setConnectingVisitType((option ?? "NEW") as "NEW" | "EXISTING");
						}}
					/>
				</Grid>
			</Grid>
		</FormGroup>

		{connectingVisitType === "EXISTING" && (
			<FormGroup label={Language("SELECT_EXISTING_VISIT")} required>
				<Select
					fetchOptions={fetchConnectingVisits}
					onChange={(option) => {
						setValue(`actions.${actionIndex}.connectingShipPortVisit.existingConnectingShipPortVisit`, option ?? undefined);
						if (option) {
							setValue(`actions.${actionIndex}.connectingShipPortVisit.agent`, option.agent ?? null);
							setValue(`actions.${actionIndex}.connectingShipPortVisit.from`, option.from ?? undefined);
							setValue(`actions.${actionIndex}.connectingShipPortVisit.until`, option.until ?? undefined);
							setValue(`actions.${actionIndex}.connectingShipPortVisit.location`, option.location ?? null);
						}
					}}
					getOptionLabel={option => {
						const from = option.from
							? moment(option.from).format("DD-MM-YYYY HH:mm")
							: intl.formatMessage({ id: "UNKNOWN_START" });

						const until = option.until
							? moment(option.until).format("HH:mm")
							: intl.formatMessage({ id: "UNKNOWN_END" });

						return `${option.location.description} - ${from} → ${until}`;
					}}
					value={action.connectingShipPortVisit?.existingConnectingShipPortVisit}
					clearable
				/>
			</FormGroup>
		)}


		<>
			<FormGroup label={Language("PORT_LOCATION")} required>
				<FormField errors={formState.errors} name={`actions.${actionIndex}.connectingShipPortVisit.location`}>
					<Controller
						name={`actions.${actionIndex}.connectingShipPortVisit.location`}
						control={control}
						rules={{ required: true }}
						render={({ field }) => (
							<LocationSelect
								{...field}
								disabled
								types={[
									LocationType.Terminal,
									LocationType.Area,
									LocationType.Port,
									LocationType.InlandTerminal,
									LocationType.Waterway,
									LocationType.Buoy,
									LocationType.ExtractionMachine,
								]}
								onChange={value => {
									field.onChange(value ? { id: value.id, description: value.description } : null);
								}}
								multiple={false}
								additionalSearchOptions={{
									isActive: true,
								}}
							/>
						)}
					/>
				</FormField>
			</FormGroup>

			<FormGroup label={Language("AGENCY")}>
				<FormField errors={formState.errors} name={`actions.${actionIndex}.connectingShipPortVisit.agency`}>
					<Controller
						name={`actions.${actionIndex}.connectingShipPortVisit.agent`}
						control={control}
						render={({ field }) => (
							<RelationSelect
								{...field}
								onChange={(value) => {
									field.onChange(value);
									syncVisitField("agent", value);
								}}
								types={[ RelationType.Agent ]}
							/>
						)} />
				</FormField>
			</FormGroup>
			<Grid container spacing={2} style={{ marginBottom: 12 }}>
				<Grid size={6}>
					<FormGroup label={Language("PORT_STAY_START")}>
						<FormField errors={formState.errors} name={`actions.${actionIndex}.connectingShipPortVisit.from`}>
							<Controller
								name={`actions.${actionIndex}.connectingShipPortVisit.from`}
								control={control}
								render={({ field }) =>
									<DatePicker
										{...field}
										onChange={(value) => {
											field.onChange(value);
											syncVisitField("from", value);
										}}
										value={field.value || undefined}
										maxDate={connectingShipPortVisit?.until ? new Date(connectingShipPortVisit?.until) : undefined}
										showTimeInput
									/>}
							/>
						</FormField>
					</FormGroup>
				</Grid>
				<Grid size={6}>
					<FormGroup label={Language("PORT_STAY_END")} >
						<FormField errors={formState.errors} name={`actions.${actionIndex}.connectingShipPortVisit.until`}>
							<Controller
								name={`actions.${actionIndex}.connectingShipPortVisit.until`}
								control={control}
								render={({ field }) =>
									<DatePicker
										{...field}
										onChange={(value) => {
											field.onChange(value);
											syncVisitField("until", value);
										}}
										value={field.value || undefined}
										showTimeInput
										minDate={connectingShipPortVisit?.from ? new Date(connectingShipPortVisit?.from) : undefined}
									/>}
							/>
						</FormField>
					</FormGroup>
				</Grid>
				{isActionDateOutsidePortStayWindow() && (
					<Grid size={12}>
						<Alert
							severity="warning"
							action={
								<Button
									size="small"
									variant="text"
									onClick={extendPortStayWindow}
								>
									{Language("EXTEND")}
								</Button>
							}
						>
							{Language("PORT_STAY_WINDOW_MISMATCH_MESSAGE")}
						</Alert>
					</Grid>
				)}
			</Grid>
			<FormGroup label={Language("NOTE")}>
				<Controller
					name={`actions.${actionIndex}.connectingVisitNotes`}
					control={control}
					render={({ field }) =>
						<Textarea {...field} value={field.value || undefined} /> }
				/>
			</FormGroup>
		</>
	</>;
};

type BerthProps = {
	formState: FormState<CargoForm>;
	control: Control<CargoForm>;
	action: CargoActionForm;
	actionIndex: number;
	setValue: UseFormSetValue<CargoForm>;
};
const Berth = (
	{
		formState,
		control,
		actionIndex,
	}: BerthProps) => {
	const values = useWatch({ control });

	return <>
		<FormGroup label={Language("BERTH_LOCATION")} required>
			<FormField errors={formState.errors} name={`actions.${actionIndex}.connectingShipBerthing.location`}>
				<Controller
					name={`actions.${actionIndex}.connectingShipBerthing.location`}
					control={control}
					rules={{ required: true }}
					render={({ field }) =>
						<LocationSelect
							{...field}
							value={field.value ?? null}
							onChange={option => field.onChange(option ?? undefined)}
							clearable
							locationGroup={values.actions?.[actionIndex]?.connectingShipPortVisit?.location?.id != null ? { id: values.actions[actionIndex].connectingShipPortVisit!.location!.id } : undefined}
							multiple={false}
							additionalSearchOptions={{
								isActive: true,
							}}
						/>
					}
				/>
			</FormField>
		</FormGroup>
		<Grid container spacing={2} style={{ marginBottom: 12 }}>
			<Grid size={6}>
				<FormGroup label={Language("BERTH_STAY_START")}>
					<FormField errors={formState.errors} name={`actions.${actionIndex}.connectingShipBerthing.window.from`}>
						<Controller
							name={`actions.${actionIndex}.connectingShipBerthing.window.from`}
							control={control}
							render={({ field }) =>
								<DatePicker
									{...field}
									value={field.value || undefined}
									showTimeInput
								/>}
						/>
					</FormField>
				</FormGroup>
			</Grid>
			<Grid size={6}>
				<FormGroup label={Language("BERTH_STAY_END")}>
					<FormField errors={formState.errors} name={`actions.${actionIndex}.connectingShipBerthing.window.to`}>
						<Controller
							name={`actions.${actionIndex}.connectingShipBerthing.window.to`}
							control={control}
							render={({ field }) =>
								<DatePicker
									{...field}
									value={field.value || undefined}
									showTimeInput
								/>}
						/>
					</FormField>
				</FormGroup>
			</Grid>
		</Grid>
	</>;
};


type ActionDateTimeWindowPickerProps = {
	control: Control<CargoForm>;
	index: number;
	formState: FormState<CargoForm>;
	setValue: UseFormSetValue<CargoForm>;
	trigger: UseFormTrigger<CargoForm>;
	action?: CargoActionForm;
	actions: CargoActionForm[];
	actionPreview?: CargoActionForm;
	validFrom?: number | null;
	validUntil?: number | null;
	getValues: UseFormGetValues<CargoForm>;
	useSimulatedTimestamps?: boolean;
	validateCargoActions: () => boolean;
}

export const ActionDateTimeWindowPicker = ({ action, actions, actionPreview, control, index, formState, setValue, validFrom, validUntil, trigger, getValues, useSimulatedTimestamps, validateCargoActions }: ActionDateTimeWindowPickerProps) => {
	if (!action) {
		return <></>;
	}

	const useRange = action.displayAsWindow ?? (action.windowFrom != null);

	const canBeSimulated = (index: number) => {
		if (!useSimulatedTimestamps) {
			return false;
		}

		// first loading action may not be simulated
		return index !== actions.findIndex(a => a.type === CallType.Loading);
	};

	const isSimulated = (action: CargoActionForm, index: number) =>
		(!!action.simulatedTimestamp || (!action.displayAsWindow && !action.date && action.windowFrom == null && action.windowTo == null))
		&& canBeSimulated(index);

	const isWindowRequired = (action: CargoActionForm, fieldToValidate: "windowFrom" | "windowTo") => {

		if (useRange && !useSimulatedTimestamps) {
			return true;
		} else if (useRange && useSimulatedTimestamps) {

			if ( action.windowFrom !== null && action.windowTo !== null) {
				return false;
			}

			if (fieldToValidate === "windowFrom") {
				return action.windowTo !== null;
			} else {
				return action.windowFrom !== null;
			}
		}
		return false;
	};

	const setSimulatedTimestampFlag = (index: number, simulated: boolean) => {
		if (!canBeSimulated(index)) {
			simulated = false;
		}

		setValue(`actions.${index}.simulatedTimestamp`, simulated);
	};

	const showPreview = !!(useSimulatedTimestamps && isSimulated(action, index));
	const displayAction = showPreview && actionPreview ? actionPreview : action;

	const datePreview =
		displayAction?.date != null
			? DateUtils.getTimestamp(displayAction.date)
			: (displayAction?.windowFrom ?? null);

	return (
		<Grid container spacing={2} style={{ marginBottom: 12 }}>
			<Grid size={12}>
				<Controller
					name={`actions.${index}.displayAsWindow`}
					control={control}
					render={({ field }) => (
						<Checkbox
							{...field}
							label={Language("USE_TIME_WINDOWS")}
							value={undefined}
							checked={field.value || undefined}
							onChange={(event) => {
								const checked = event.target.checked;
								field.onChange(checked);

								if (checked && action.date) {
									// date -> windows
									setValue(`actions.${index}.windowFrom`, DateUtils.getTimestamp(moment(action.date).startOf("day")));
									setValue(`actions.${index}.windowTo`, DateUtils.getTimestamp(moment(action.date).endOf("day")));
									setValue(`actions.${index}.date`, null);
								} else if (!checked && action.windowFrom) {
									// windows -> date
									setValue(`actions.${index}.date`, moment(action.windowFrom).format("YYYY-MM-DD"));
									setValue(`actions.${index}.windowFrom`, null);
									setValue(`actions.${index}.windowTo`, null);
								} else {
									// windows -> windows (legacy)
									// date -> date (legacy)
									// do nothing, already in the right format data-wise, just not visually
								}
							}}
						/>)} />
			</Grid>
			<Grid size={12}>
				{!useRange ? (
					<FormGroup label={Language(`ORDER_${action.type}_DATE`)} overflow="hidden" required={!canBeSimulated(index)}>
						<FormField errors={formState.errors} name={`actions.${index}.date`}>
							<Controller
								name={`actions.${index}.date`}
								control={control}
								rules={{ required: !canBeSimulated(index) } }
								render={({ field }) => (
									<DatePicker
										{...field}
										placeholderText={showPreview && datePreview
											? moment(datePreview).format("L")
											: undefined}
										minDate={validFrom ? new Date(validFrom) : undefined}
										maxDate={validUntil ? new Date(validUntil) : undefined}
										onChange={(value) => {
											const newDate = value != null ? moment(value).format("YYYY-MM-DD") : null;

											setSimulatedTimestampFlag(index, newDate == null);
											field.onChange(newDate);

											validateCargoActions();
										}}
										value={showPreview
											? undefined
											: (action.date ? (DateUtils.getTimestamp(action.date) || undefined) : undefined)} />
								)} />

							{showPreview ? <SimulatedTimestampHelperText field={"DATE"} /> : <></>}
						</FormField>
					</FormGroup>
				) : (
					<Grid container spacing={2}>
						<Grid size={6}>
							<FormGroup required={isWindowRequired(action, "windowFrom") || !canBeSimulated(index)} label={Language(`ORDER_${action.type}_WINDOW_FROM`)} overflow="hidden">
								<FormField errors={formState.errors} name={`actions.${index}.windowFrom`}>
									<Controller
										name={`actions.${index}.windowFrom`}
										control={control}
										rules={{ required: isWindowRequired(action, "windowFrom") || !canBeSimulated(index) }}
										render={({ field }) => (
											<DatePicker
												{...field}
												placeholderText={
													showPreview && displayAction?.windowFrom
														? moment(displayAction.windowFrom).format("L LT")
														: undefined
												}
												value={
													showPreview
														? undefined
														: (action.windowFrom ?? undefined)
												}
												showTimeInput
												minDate={validFrom ? new Date(validFrom) : undefined}
												maxDate={validUntil ? new Date(validUntil) : undefined}
												onChange={(value) => {
													const newWindowFrom = value ?? null;
													const currentWindowTo = getValues(`actions.${index}.windowTo`) ?? null;

													setSimulatedTimestampFlag(index, newWindowFrom == null && currentWindowTo == null);

													if (newWindowFrom != null && (currentWindowTo == null || currentWindowTo < newWindowFrom)) {
														setValue(`actions.${index}.windowTo`, DateUtils.getTimestamp(moment(newWindowFrom).add(1, "hour")));
													}

													field.onChange(value);

													if (newWindowFrom != null && currentWindowTo == null) {
														trigger(`actions.${index}.windowTo`);
													}

													validateCargoActions();
												}}
											/>
										)}
									/>
									{showPreview ? <SimulatedTimestampHelperText field={"WINDOW_START"} /> : <></>}
								</FormField>
							</FormGroup>
						</Grid>
						<Grid size={6}>
							<FormGroup required={isWindowRequired(action, "windowTo") || !canBeSimulated(index)} label={Language(`ORDER_${action.type}_WINDOW_TO`)} overflow="hidden">
								<FormField errors={formState.errors} name={`actions.${index}.windowTo`}>
									<Controller
										name={`actions.${index}.windowTo`}
										control={control}
										rules={{
											required: isWindowRequired(action, "windowTo") || !canBeSimulated(index),
											validate: (windowTo, cargo) => {
												if (moment(windowTo).isBefore(cargo.actions[index].windowFrom)) {
													return intl.formatMessage({ id: "ACTION_WINDOW_TO_TIME_MUST_BE_AFTER_ACTION_WINDOW_FROM" });
												}
											},
										}}
										render={({ field }) => (
											<DatePicker
												{...field}
												placeholderText={
													showPreview && displayAction?.windowTo
														? moment(displayAction.windowTo).format("L LT")
														: undefined
												}
												minDate={DateUtils.getDateOrUndefined(Math.max(validFrom ?? 0, action.windowFrom ?? 0))}
												maxDate={DateUtils.getDateOrUndefined(validUntil)}
												onChange={(value) => {
													const newWindowTo = value ?? null;
													const currentWindowFrom = getValues(`actions.${index}.windowFrom`) ?? null;

													field.onChange(value);

													setSimulatedTimestampFlag(index, currentWindowFrom == null && newWindowTo == null);

													if (value && !currentWindowFrom) {
														trigger(`actions.${index}.windowFrom`);
													}

													validateCargoActions();
												}}
												value={
													showPreview
														? undefined
														: (action.windowTo ?? undefined)
												}
												showTimeInput />
										)}
									/>
									{showPreview ? <SimulatedTimestampHelperText field={"WINDOW_END"} /> : <></>}
								</FormField>
							</FormGroup>
						</Grid>
					</Grid>
				)}
			</Grid>
		</Grid>
	);
};

type Route = {
	distanceKm?: number | null;
	transitTimeInMinutes?: number | null;
}

export const RouteInfo = ({ currentLocationId, nextLocationId, setRouteInfo }: {
	setRouteInfo?: (value: Route) => void;
	currentLocationId?: number;
	nextLocationId?: number;
}) => {

	const [ route, setRoute ] = useState<Route>({
		distanceKm: 0,
		transitTimeInMinutes: 0,
	});

	const [ fetchBatchRoutes, { loading } ] = useLazyQuery(GetBatchRoutesDocument);

	useEffect(() => {
		fetchBatchRoutes({
			variables: {
				input: {
					routes: [ { from: currentLocationId, to: nextLocationId } ],
				},
			},
		}).then(({ data }) => {
			const currentRoute = data?.getBatchRoutes?.[0].route || {
				distanceKm: 0,
				transitTimeInMinutes: 0,
			};

			setRoute(currentRoute);

			if (setRouteInfo) {
				setRouteInfo(currentRoute);
			}
		});
	}, [ currentLocationId, nextLocationId ]);

	return (
		<div className="route-info-wrapper">
			<div className="route-info">
				{loading ? (
					<CircularProgress size={20} />
				) : (
					<div>
						<p>
							<small>{numberOrNull(route.distanceKm) ? DataFormat.distance(route.distanceKm) : undefined}</small>
						</p>
						<p>
							<small>{numberOrNull(route.transitTimeInMinutes) ? DataFormat.minutesToTime(route.transitTimeInMinutes) : undefined}</small>
						</p>
					</div>
				)}
			</div>
		</div>
	);
};

const CONNECTING_SHIP_PORT_VISIT_QUERY = gql(`#graphql
query actions_connectingShipPortVisits($search: ConnectingShipPortVisitSearch!, $pagination: Pagination) {
	connectingShipPortVisits(search: $search, pagination: $pagination) {
		id
		ship {
			id
			name
		}
		location {
			id
			description
			type
			locationGroups {
				id
				type
				description
			}
		}
		agent {
			id
			name
		}
		from
		until
	}
}
`);

const SimulatedTimestampHelperText = ({ field }: { field: "DATE" | "WINDOW_START" | "WINDOW_END" }) => {
	return <span
		style={{
			minHeight: 14,
			marginTop: 4,
			fontSize: 10,
			color: "grey",
		}}
	>
		{intl.formatMessage({ id: `SIMULATED_${field}_HELPER_TEXT` })}
	</span>;
};

export default Actions;
